<template>
    <v-card class="resource-card" outlined hover :class="{ 'pinned-card': resource.pinned }">
        <v-card-text class="pa-4">
            <!-- Header with file type icon and pin button -->
            <div class="d-flex justify-space-between align-start mb-3">
                <div class="d-flex align-center">
                    <v-icon :color="getFileTypeColor(resource.file_type)" size="32" class="mr-3">
                        {{ getFileTypeIcon(resource.file_type) }}
                    </v-icon>
                    <div>
                        <h3 class="text-subtitle-1 font-weight-bold mb-1 resource-title">
                            {{ resource.name }}
                        </h3>
                        <div class="d-flex align-center">
                            <v-chip :color="getCategoryColor(resource.category)" small text-color="white" class="mr-2">
                                {{ resource.category }}
                            </v-chip>
                            <span v-if="companyName" class="text-caption grey--text">
                                {{ companyName }}
                            </span>
                            <span v-else class="text-caption blue--text">
                                Shared
                            </span>
                        </div>
                    </div>
                </div>

                <!-- Pin toggle button -->
                <v-btn icon small outlined @click.stop="$emit('pin-toggle', resource.id)" class="pin-btn">
                    <v-icon :color="resource.pinned ? 'amber' : 'grey lighten-1'" small>
                        {{ resource.pinned ? 'mdi-pin' : 'mdi-pin-outline' }}
                    </v-icon>
                </v-btn>
            </div>

            <!-- Description -->
            <p class="text-body-2 grey--text text--darken-1 mb-3 resource-description">
                {{ resource.description }}
            </p>

            <!-- Tags -->
            <div v-if="normalizedTags && normalizedTags.length > 0" class="mb-3">
                <div class="d-flex flex-wrap gap-1">
                    <v-chip v-for="tag in normalizedTags.slice(0, 3)" :key="tag.id || tag" x-small outlined color="primary"
                        class="tag-chip">
                        {{ typeof tag === 'object' ? tag.tag : tag }}
                    </v-chip>
                    <v-chip v-if="normalizedTags.length > 3" x-small outlined color="grey" class="tag-chip">
                        +{{ normalizedTags.length - 3 }}
                    </v-chip>
                </div>
            </div>

            <!-- File details -->
            <div class="d-flex justify-space-between align-center text-caption grey--text mb-3">
                <span>{{ isUrlResource ? 'URL Resource' : resource.size }}</span>
                <span>{{ formatDate(resource.created_at) }}</span>
            </div>

            <!-- Actions -->
            <div class="d-flex justify-space-between align-center flex-wrap">
                <div class="text-caption grey--text mb-2 mb-sm-0">
                    by {{ resource.created_by }}
                </div>

                <div class="d-flex flex-wrap gap-1 action-buttons">
                    <!-- URL Resource actions -->
                    <template v-if="isUrlResource">
                        <!-- Open URL button -->
                        <v-btn x-small color="primary" outlined :href="resource.url" target="_blank" class="view-btn">
                            <v-icon left x-small>mdi-open-in-new</v-icon>
                            Open
                        </v-btn>
                    </template>

                    <!-- File Resource actions -->
                    <template v-else>
                        <!-- Download button -->
                        <v-btn x-small color="primary" outlined @click.stop="$emit('download', resource)"
                            class="download-btn">
                            <v-icon left x-small>mdi-download</v-icon>
                            Download
                        </v-btn>

                        <!-- External link for URLs -->
                        <v-btn v-if="resource.url" x-small color="primary" text :href="getViewUrl(resource.url)"
                            target="_blank" class="view-btn">
                            <v-icon left x-small>mdi-open-in-new</v-icon>
                            View
                        </v-btn>
                    </template>

                    <!-- Edit button (for both types) -->
                    <v-btn x-small color="orange" text @click.stop="$emit('edit', resource)" class="edit-btn">
                        <v-icon x-small>mdi-pencil</v-icon>
                    </v-btn>

                    <!-- Delete button (for both types) -->
                    <v-btn x-small color="red" text @click.stop="$emit('delete', resource)" class="delete-btn">
                        <v-icon x-small>mdi-delete</v-icon>
                    </v-btn>
                </div>
            </div>
        </v-card-text>

        <!-- Loading overlay for uploads -->
        <v-overlay v-if="resource.uploading" absolute color="white" opacity="0.8">
            <div class="text-center">
                <v-progress-circular indeterminate color="primary" size="40"></v-progress-circular>
                <p class="mt-2 text-body-2">Uploading...</p>
            </div>
        </v-overlay>
    </v-card>
</template>

<script>
export default {
    name: 'ResourceCard',
    inject: ['companies'],
    props: {
        resource: {
            type: Object,
            required: true
        }
    },
    computed: {
        normalizedTags() {
            if (!this.resource.tags) return [];

            // If tags is already an array
            if (Array.isArray(this.resource.tags)) {
                // Check if it's an array of tag objects with a 'tag' property
                if (this.resource.tags.length > 0 && typeof this.resource.tags[0] === 'object' && this.resource.tags[0].tag) {
                    return this.resource.tags;
                }
                // If it's an array of strings
                return this.resource.tags;
            }

            // If tags is a string, try to parse it
            if (typeof this.resource.tags === 'string') {
                try {
                    // Try parsing as JSON first
                    const parsed = JSON.parse(this.resource.tags);
                    if (Array.isArray(parsed)) {
                        return parsed;
                    }
                } catch (e) {
                    // If JSON parse fails, split by comma and clean up
                    return this.resource.tags
                        .split(',')
                        .map(tag => tag.trim())
                        .filter(tag => tag.length > 0);
                }
            }

            return [];
        },
        companyName() {
            if (!this.resource.company_id) return null;
            
            const companies = this.companies() || [];
            const company = companies.find(c => c.id === this.resource.company_id);
            return company ? company.name : `Company ${this.resource.company_id}`;
        },
        isUrlResource() {
            return this.resource && (this.resource.type === 'url' || (this.resource.url && !this.resource.file_path && !this.resource.size));
        }
    },
    methods: {
        getFileTypeIcon(fileType) {
            const iconMap = {
                pdf: 'mdi-file-pdf-box',
                doc: 'mdi-file-word-box',
                docx: 'mdi-file-word-box',
                xls: 'mdi-file-excel-box',
                xlsx: 'mdi-file-excel-box',
                ppt: 'mdi-file-powerpoint-box',
                pptx: 'mdi-file-powerpoint-box',
                txt: 'mdi-file-document-outline',
                zip: 'mdi-folder-zip',
                image: 'mdi-file-image-box',
                video: 'mdi-file-video-box',
                audio: 'mdi-file-music-box',
                // URL-based resource icons
                'google-sheets': 'mdi-google-spreadsheet',
                'google-docs': 'mdi-file-document',
                'google-slides': 'mdi-google-slides',
                'google-drive': 'mdi-google-drive',
                'office-365': 'mdi-microsoft-office',
                'url': 'mdi-link'
            };
            return iconMap[fileType] || 'mdi-file-document-outline';
        },
        getFileTypeColor(fileType) {
            const colorMap = {
                pdf: 'red',
                doc: 'blue',
                docx: 'blue',
                xls: 'green',
                xlsx: 'green',
                ppt: 'orange',
                pptx: 'orange',
                txt: 'grey',
                zip: 'purple',
                image: 'pink',
                video: 'deep-purple',
                audio: 'teal',
                // URL-based resource colors
                'google-sheets': 'green',
                'google-docs': 'blue',
                'google-slides': 'orange',
                'google-drive': 'blue',
                'office-365': 'red',
                'url': 'indigo'
            };
            return colorMap[fileType] || 'grey';
        },
        getCategoryColor(category) {
            const colorMap = {
                'SOP': 'blue',
                'Template': 'green',
                'EOD': 'orange',
                'SOD': 'purple',
                'Guide': 'teal',
                'Other': 'grey'
            };
            return colorMap[category] || 'primary';
        },
        formatDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        },
        getViewUrl(url) {
            // If URL already has a protocol, use it as-is
            if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
                return url;
            }
            // Otherwise, prepend the API base URL
            const apiBaseUrl = process.env.VUE_APP_API_URL || '';
            return url ? `${apiBaseUrl}${url}` : '#';
        }
    }
};
</script>

<style scoped>
.resource-card {
    position: relative;
    border-radius: 12px;
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.resource-card .v-card__text {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 16px !important;
}

.resource-card:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
}

.pinned-card {
    border-color: #FFC107;
    background: linear-gradient(135deg, #FFF8E1 0%, #FFFFFF 100%);
}

.pin-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    background: rgba(255, 193, 7, 0.1);
    border-radius: 50%;
    padding: 4px;
}

.resource-title {
    line-height: 1.3;
    max-width: 180px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
}

.resource-description {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
    min-height: 2.8em;
    word-break: break-word;
}

.tag-chip {
    margin: 2px;
    font-size: 10px;
    height: 20px;
}

.pin-btn {
    transition: all 0.2s ease;
}

.pin-btn:hover {
    background-color: rgba(255, 193, 7, 0.1);
}

.download-btn {
    text-transform: none;
    font-weight: 500;
    border-radius: 6px;
    min-width: auto !important;
    padding: 0 8px !important;
    height: 24px !important;
}

.view-btn {
    text-transform: none;
    font-weight: 500;
    min-width: auto !important;
    padding: 0 8px !important;
    height: 24px !important;
}

.delete-btn {
    text-transform: none;
    font-weight: 500;
    transition: all 0.2s ease;
    min-width: auto !important;
    padding: 0 8px !important;
    height: 24px !important;
}

.delete-btn:hover {
    background-color: rgba(244, 67, 54, 0.1);
}

.edit-btn {
    text-transform: none;
    font-weight: 500;
    transition: all 0.2s ease;
    min-width: auto !important;
    padding: 0 8px !important;
    height: 24px !important;
}

.edit-btn:hover {
    background-color: rgba(255, 152, 0, 0.1);
}

.action-buttons {
    gap: 4px;
}

.action-buttons .v-btn {
    font-size: 10px !important;
}

.gap-1>* {
    margin-right: 4px;
    margin-bottom: 4px;
}

.gap-2>* {
    margin-right: 8px;
}

.gap-2>*:last-child {
    margin-right: 0;
}

/* Mobile responsiveness */
@media (max-width: 600px) {
    .resource-title {
        font-size: 14px;
        max-width: 140px;
    }

    .resource-description {
        font-size: 12px;
    }

    .download-btn {
        font-size: 9px !important;
        padding: 0 6px !important;
        height: 22px !important;
    }

    .view-btn {
        font-size: 9px !important;
        padding: 0 6px !important;
        height: 22px !important;
    }

    .edit-btn {
        font-size: 9px !important;
        padding: 0 4px !important;
        height: 22px !important;
    }

    .delete-btn {
        font-size: 9px !important;
        padding: 0 4px !important;
        height: 22px !important;
    }

    .action-buttons {
        gap: 2px;
        flex-wrap: wrap;
        justify-content: flex-end;
    }

    .v-card-text {
        padding: 12px !important;
    }
}

@media (max-width: 400px) {
    .resource-title {
        max-width: 120px;
        font-size: 13px;
    }

    .action-buttons .v-btn .v-btn__content {
        font-size: 8px !important;
    }
}
</style>